import QuizInteractiveFlow from "@/components/storyboard/quiz/QuizInteractiveFlow";
import QuizCodeExplanation from "@/components/storyboard/quiz/QuizCodeExplanation";
import { useCappedCodeQuiz } from "@/hooks/useCappedCodeQuiz";
import type { ValidationResult } from "@/services/QuizValidationService";
import type { QuizCodeExplanationEntryData } from "@/types/storyboard";

export interface QuizCodeExplanationFlowProps {
  id: string;
  title: string;
  entries: QuizCodeExplanationEntryData[];
  onValidate: (
    explanation: string,
    entryIndex: number
  ) => Promise<ValidationResult>;
}

/**
 * Code-explanation quiz embedded in the shared interactive-element flow. The
 * textarea is seeded from the learner's saved submissions; "Erklärung prüfen"
 * runs server-side (LLM) validation, which also persists the answer. No
 * localStorage — durable state lives in validation_data, and so do the verdicts
 * the card recaps.
 *
 * Each task grants a limited number of tries, tracked per task: the learner can
 * move between tasks freely, and the quiz ends once none of them can be worked
 * on any more.
 */
const QuizCodeExplanationFlow = ({
  id,
  title,
  entries,
  onValidate,
}: QuizCodeExplanationFlowProps) => {
  const {
    submissions,
    refresh,
    statusOverride,
    completedCtaLabel,
    showCloseCta,
  } = useCappedCodeQuiz({
    id,
    prompts: entries.map((entry) => entry.challenge),
    answerKind: "text",
  });

  return (
    <QuizInteractiveFlow
      id={id}
      title={title}
      illustrationKind="code_explanation"
      size="wide"
      supportsInProgress
      statusOverride={statusOverride}
      completedCtaLabel={completedCtaLabel}
      showCloseCta={showCloseCta}
      showCompletedBadge={false}
      renderQuiz={() => (
        <QuizCodeExplanation
          entries={entries}
          onValidate={onValidate}
          initialSubmissions={submissions}
          onSubmitted={refresh}
        />
      )}
    />
  );
};

export default QuizCodeExplanationFlow;
